import { ApiSet, BaseResponse, IndexApiSet, useDownloadApi, useIndexApi, usePatchApi, usePostApi, usePutApi, useShowApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { BulkSampleForm, Sample, SampleForm, SampleSearchForm, SampleStatus } from 'entities/sample'
import { TranscriptionForm } from 'components/shared/type'
import { Rack } from 'entities/rack'
import { DisposeSampleForm } from './rack'

type SamplesResponse = PagingResponse & {
    results: Sample[]
}

export function useFetchSamplesApi(searchForm: Form<SampleSearchForm>): IndexApiSet<SamplesResponse> & { execute: () => void } {
    const apiPath = 'samples/'
    const api = useIndexApi<SamplesResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm.object.page || 1, perPage: searchForm.object.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm.object })
    }

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm.object])

    return { ...api, execute: execute }
}

export function useFetchDetailSamplesApi(searchForm: Form<SampleSearchForm>): IndexApiSet<SamplesResponse> & { execute: () => void } {
    const apiPath = 'samples/detail_list/'
    const api = useIndexApi<SamplesResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm.object.page || 1, perPage: searchForm.object.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm.object })
    }

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm.object])

    return { ...api, execute: execute }
}

type SampleResponse = BaseResponse & Sample

export function useFetchSampleApi(): ApiSet<SampleResponse> & { execute: (id: number) => void } {
    const api = useShowApi<SampleResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `samples/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export function usePatchSampleApi(): ApiSet<BaseResponse> & { execute: (form: Form<SampleForm>) => void } {
    const apiSet = usePatchApi<BaseResponse, SampleForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<SampleForm>) => {
        const apiPath = `samples/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function usePostToExtractionApi(): ApiSet<SampleResponse> & { execute: (ids: Form<TranscriptionForm>) => void } {
    const apiPath = 'samples/to_extraction/'
    const api = usePostApi<SampleResponse, TranscriptionForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TranscriptionForm>): void => {
        api.execute(apiPath, form)
    }

    return { ...api, execute: execute }
}

export function useBulkPostSampleApi(): ApiSet<SampleResponse> & { execute: (form: Form<BulkSampleForm>) => void } {
    const apiPath = 'samples/inspect/'
    const api = usePostApi<SampleResponse, BulkSampleForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<BulkSampleForm>): void => {
        api.execute(apiPath, form)
    }

    return { ...api, execute: execute }
}

export function useDownloadCsvSamplesApi(searchForm: Form<SampleSearchForm>): ApiSet<SamplesResponse> & { execute: () => void } {
    const apiPath = 'samples/csv/'
    const api = useDownloadApi<SamplesResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath, 'samples', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostSampleApi(): ApiSet<SampleResponse> & { execute: (form: Form<SampleForm>) => void } {
    const apiSet = usePostApi<SampleResponse, SampleForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<SampleForm>) => {
        const apiPath = `samples/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutSampleApi(): ApiSet<SampleResponse> & { execute: (form: Form<SampleForm>) => void } {
    const apiSet = usePutApi<SampleResponse, SampleForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<SampleForm>) => {
        const apiPath = `samples/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

type DefinitionsResponse = BaseResponse & {
    collectionTimings: string[]
}

type NextRackResponse = {
    rack: Rack
    tubeLocation?: string
}

type NextLocationResponse = BaseResponse & {
    ngsRack?: NextRackResponse
    pcrRack?: NextRackResponse
}

export function useFetchLastLocationApi(): ApiSet<NextLocationResponse> & {
    execute: () => void
} {
    const api = useShowApi<NextLocationResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        const apiPath = `samples/last_location/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export function usePutInspectSampleApi(): ApiSet<SampleResponse> & { execute: (form: Form<SampleForm[]>) => void } {
    const apiSet = usePutApi<SampleResponse, SampleForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<SampleForm[]>) => {
        const apiPath = `samples/inspect/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function usePutInputSampleApi(): ApiSet<SampleResponse> & { execute: (form: Form<SampleForm>) => void } {
    const apiSet = usePutApi<SampleResponse, SampleForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<SampleForm>) => {
        const apiPath = `samples/input/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function usePutConfirmedSampleApi(): ApiSet<SampleResponse> & { execute: (id: number) => void } {
    const apiSet = usePutApi<SampleResponse, SampleForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (id: number) => {
        const apiPath = `samples/${id}/confirmed/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePutRejectSampleApi(): ApiSet<SampleResponse> & { execute: (id: number) => void } {
    const apiSet = usePutApi<SampleResponse, SampleForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (id: number) => {
        const apiPath = `samples/${id}/reject/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function useDownloadSampleListApi(searchForm: Form<SampleSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        const apiPath = `sample_exports/`
        api.execute(apiPath, `samples`, searchForm.object)
    }

    return { ...api, execute: execute }
}

export type UpdateSampleStatusForm = {
    items?: Sample[]
    status?: SampleStatus
}
export function useUpdateSampleStatusApi(): ApiSet<SampleResponse> & { execute: (form: Form<UpdateSampleStatusForm>) => void } {
    const apiSet = usePatchApi<SampleResponse, UpdateSampleStatusForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<UpdateSampleStatusForm>) => {
        const apiPath = `samples/update_status/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export type UpdateSamplePriorityForm = {
    items?: Sample[]
    priority?: number
}
export function useUpdateSamplePriorityApi(): ApiSet<SampleResponse> & { execute: (form: Form<UpdateSamplePriorityForm>) => void } {
    const apiSet = usePatchApi<SampleResponse, UpdateSamplePriorityForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<UpdateSampleStatusForm>) => {
        const apiPath = `samples/update_priority/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export type RecollectForm = {
    newRequestFormCode?: string
}
export function useRecollectSampleApi(): ApiSet<SampleResponse> & { execute: (sampleId: number, form: Form<RecollectForm>) => void } {
    const apiSet = usePatchApi<SampleResponse, RecollectForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (sampleId: number, form: Form<RecollectForm>) => {
        const apiPath = `samples/${sampleId}/recollect/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDisposeSampleApi(): ApiSet<BaseResponse> & {
    execute: (id: number, form: Form<DisposeSampleForm>) => void
} {
    const api = usePatchApi<BaseResponse, DisposeSampleForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number, form: Form<DisposeSampleForm>): void => {
        const apiPath = `samples/${id}/dispose/`
        api.execute(apiPath, form.object)
    }

    return { ...api, execute: execute }
}

export function useBulkPutSamplesApi(): ApiSet<BaseResponse> & { execute: (form: Form<SampleForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, SampleForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<SampleForm[]>) => {
        const apiPath = `samples/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export type UploadResultPdfList = {
    resultPdfList?: File[]
}

export function useUploadResultPdfListApi(): ApiSet<BaseResponse> & { execute: (form: Form<UploadResultPdfList>) => void } {
    const apiSet = usePutApi<BaseResponse, UploadResultPdfList>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<UploadResultPdfList>) => {
        const apiPath = `samples/upload_result_pdf_list/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export type AutoResultPdfUploadForm = {
    sampleIds: number[]
}

export function useAutoResultPdfUploadApi(): ApiSet<BaseResponse> & { execute: (form: Form<AutoResultPdfUploadForm>) => void } {
    const apiSet = usePutApi<BaseResponse, AutoResultPdfUploadForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<AutoResultPdfUploadForm>) => {
        const apiPath = `samples/auto_upload_result_pdf/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function usePutSampleResultStatusApi(): ApiSet<BaseResponse> & { execute: (form: Form<SampleForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, SampleForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<SampleForm[]>) => {
        const apiPath = `samples/update_result_status/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function usePutSampleResultConfirmApi(): ApiSet<BaseResponse> & { execute: (form: Form<SampleForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, SampleForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<SampleForm[]>) => {
        const apiPath = `samples/update_result_confirm/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}
